import { useState } from "react";
import { useQuery, useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { Copy, Check, X } from "lucide-react";
import toast from "react-hot-toast";
import { apiService } from "../services/api";
import TypeaheadUserInput from "../components/TypeaheadUserInput";
import TypeaheadPrefixInput from "../components/TypeaheadPrefixInput";
import type { DtAPIKeyCreateResponse, User } from "../types";

export default function CreateDtApiKeyPage() {
  const navigate = useNavigate();
  const [owner, setOwner] = useState("");
  const [prefixes, setPrefixes] = useState<string[]>([]);
  const [selectedOwnerIds, setSelectedOwnerIds] = useState<string[]>([]);
  const [ownerPickKey, setOwnerPickKey] = useState(0);
  const [createdKey, setCreatedKey] = useState<DtAPIKeyCreateResponse | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  const { data: usersData } = useQuery("users", () => apiService.getUsers());
  const allUsers: User[] = usersData?.data?.data || [];

  const createMutation = useMutation(
    () =>
      apiService.createDtAPIKey({
        owner,
        prefixes,
        ownerIds: selectedOwnerIds,
      }),
    {
      onSuccess: (res) => {
        const data = res.data?.data as DtAPIKeyCreateResponse;
        setCreatedKey(data);
        toast.success("API key created");
      },
      onError: (_error: any) => {
        toast.error("Failed to create API key");
      },
    },
  );

  const handleRemovePrefix = (index: number) => {
    setPrefixes(prefixes.filter((_, i) => i !== index));
  };

  const handleCopyKey = async () => {
    if (createdKey?.apiKey?.rawKey) {
      await navigator.clipboard.writeText(createdKey.apiKey.rawKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (createdKey) {
    return (
      <div className="space-y-6 max-w-2xl">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          API Key Created
        </h1>

        <div className="card p-6 space-y-4">
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
              Copy this API key and share it with the team.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-sm font-mono break-all">
                {createdKey.apiKey.rawKey}
              </code>
              <button onClick={handleCopyKey} className="btn btn-outline p-2">
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              <strong>Owner:</strong> {createdKey.apiKey.owner}
            </p>
            {createdKey.apiKey.prefixes?.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  Allowed Prefixes:
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {createdKey.apiKey.prefixes.map((p) => (
                    <span
                      key={p.id}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                    >
                      {p.allowedPrefix}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate("/dt-api-keys")}
            className="btn btn-primary"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Create Dependency Track API Key
      </h1>

      <div className="card p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Owner (team name)
          </label>
          <input
            type="text"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="e.g. rock-drills"
            className="input w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Allowed Prefixes
          </label>
          <TypeaheadPrefixInput
            onSelect={(value) => {
              if (!prefixes.includes(value)) {
                setPrefixes([...prefixes, value]);
              }
            }}
            excludeValues={prefixes}
          />
          <div className="flex flex-wrap gap-2">
            {prefixes.map((prefix, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 text-sm"
              >
                {prefix}
                <button
                  onClick={() => handleRemovePrefix(i)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Additional Owners
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            You will be added as an owner automatically. Add other users who
            should be able to view and manage this key.
          </p>
          <TypeaheadUserInput
            key={ownerPickKey}
            id="owner-search"
            name="owner-search"
            value=""
            onChange={(value: string) => {
              const matchedUser = allUsers.find((u) => u.id === value);
              if (!matchedUser) return;
              if (!selectedOwnerIds.includes(value)) {
                setSelectedOwnerIds([...selectedOwnerIds, value]);
              }
              setOwnerPickKey((k) => k + 1);
            }}
            users={allUsers.filter((u) => !selectedOwnerIds.includes(u.id))}
            placeholder="Search users..."
          />
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedOwnerIds.map((uid) => {
              const user = allUsers.find((u) => u.id === uid);
              return (
                <span
                  key={uid}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 text-sm"
                >
                  {user?.name || uid}
                  <button
                    onClick={() =>
                      setSelectedOwnerIds(
                        selectedOwnerIds.filter((id) => id !== uid),
                      )
                    }
                    className="text-blue-400 hover:text-blue-600 dark:hover:text-blue-200"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            onClick={() => createMutation.mutate()}
            disabled={!owner.trim() || createMutation.isLoading}
            className="btn btn-primary"
          >
            {createMutation.isLoading ? "Creating..." : "Create API Key"}
          </button>
          <button
            onClick={() => navigate("/dt-api-keys")}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
